import { FC, useState } from 'react';
import { useOne, useNavigation, useTranslate, useUpdate, useNotification } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { FormBuilder, Loading, SomethingWentWrong } from '~/components';
import { resourceNames } from '~/resources/resource-names';
import { Asset, Challenge, getSdk, UpdateChallengeInput } from '~/api';
import { challengeFormBuilder } from '../form';
import { challengeFields } from '../challenge-resource';
import { GQLClient } from '~/config/gql-client';
import { ChallengeLinksForm, validateChallengeLinks } from '~/components/ChallengeLinksForm';

export const ChallengeEditPage: FC = () => {
  // ======================= HOOKS
  const { id } = useParams();
  const navigation = useNavigation();
  const t = useTranslate();
  const notif = useNotification();
  const [handlingAsset, setHandlingAsset] = useState(false);
  const [deletedLinkIds, setDeletedLinkIds] = useState<string[]>([]);

  if (!id) return <SomethingWentWrong />;

  // ======================= API
  const client = GQLClient.getInstance();
  const { mutate: updateChallenge, isLoading: updating } = useUpdate();
  const { data, isLoading } = useOne<Challenge>({
    resource: resourceNames.challenge,
    id,
    metaData: { fields: challengeFields },
  });

  const mainData = data?.data;
  
  if (isLoading || !mainData) return <Loading />;

  // ======================= EVENTS
  const onUpdate = async (data: any) => {
    const { 
      title, 
      description, 
      startDate, 
      endDate, 
      enabled, 
      type, 
      limitPerCustomer,
      asset,
      links,
      outletId
    } = data;

    // Validate challenge links
    if (!validateChallengeLinks(links)) {
      notif?.open?.({
        message: t('challenges.errors.linksRequired', { 
          fallback: 'At least one challenge link with URL and platform is required' 
        }),
        type: 'error',
      });
      return;
    }

    // Handle asset upload if provided
    const shouldCreateAsset = !!asset?.[0] && typeof asset[0] !== 'string';
    let assetId;
    
    if (shouldCreateAsset) {
      setHandlingAsset(true);
      try {
        const assetRes = await getSdk(client).createAssets({ input: [{ file: asset[0] }] });
        const assetsData = assetRes?.createAssets as Asset[];

        if (!assetsData?.[0]?.id) {
          console.warn(`[Failed to get asset]`, assetsData);
          notif?.open?.({
            message: t('common.somethingWrong', { fallback: 'Something went wrong, Please try again' }),
            type: 'error',
          });
          setHandlingAsset(false);
          return;
        }
        assetId = assetsData[0].id;
      } catch (error) {
        console.error('[Asset upload error]', error);
        notif?.open?.({
          message: t('common.somethingWrong', { fallback: 'Failed to upload image' }),
          type: 'error',
        });
        setHandlingAsset(false);
        return;
      }
    }

    // Process links - they come directly as array from ChallengeLinksForm
    const processedLinks = [];
    if (links && Array.isArray(links)) {
      for (const link of links) {
        // Only add links with URL and platformId
        if (link.url && link.platformId) {
          const processedLink: any = {
            platformId: link.platformId,
            url: link.url,
            title: link.title || undefined,
            description: link.description || undefined,
          };
          
          // Include ID if it's an existing link
          if (link.id) {
            processedLink.id = link.id;
          }
          
          processedLinks.push(processedLink);
        }
      }
    }

    // Prepare challenge input
    const challengeInput: any = {
      id,
      title,
      description,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      enabled,
      type,
      limitPerCustomer: limitPerCustomer !== undefined && limitPerCustomer !== '' && limitPerCustomer !== null ? Number(limitPerCustomer) : null,
      assetId: assetId || undefined,
      outletId
    };

    // Add links to the input if we have any
    if (processedLinks.length > 0) {
      challengeInput.links = processedLinks;
    }

    updateChallenge(
      {
        id,
        resource: resourceNames.challenge,
        values: challengeInput,
        meta: {
          deletedLinkIds: deletedLinkIds,
        },
        errorNotification: {
          message: t('challenges.update.failed', { fallback: 'Failed to update challenge' }),
          type: 'error',
        },
        successNotification: {
          message: t('challenges.update.success', { fallback: 'Challenge updated successfully' }),
          type: 'success',
        },
      },
      {
        onSuccess: () => navigation.list(resourceNames.challenge),
        onSettled: () => setHandlingAsset(false),
      }
    );
  };

  return (
    <FormBuilder
      isUpdate
      resourceName={resourceNames.challenge}
      title={t('challenges.update.name', { fallback: 'Update Challenge' })}
      loading={updating || handlingAsset}
      onSubmit={onUpdate}
      items={(formHook) => challengeFormBuilder(mainData, formHook)}
      extra={[
        {
          key: 'links',
          render: ({ setValue, watch, register }) => {
            // Register the field
            register('links');
            
            const links = watch('links') || mainData?.links || [];
            
            return (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('challenges.form.links', { fallback: 'Challenge Links' })}
                </h3>
                <ChallengeLinksForm 
                  value={links} 
                  onChange={(newLinks) => setValue('links', newLinks)}
                  onDeletedLinksChange={setDeletedLinkIds}
                  initialLinks={mainData?.links || []}
                />
              </div>
            );
          },
        },
      ]}
    />
  );
}; 